import { Button, Tooltip, type ButtonProps } from '@mui/material'
import {
  createLink,
  useRouter,
  type AnyRoute,
  type LinkComponent,
  type LinkProps,
  type StaticDataRouteOption,
} from '@tanstack/react-router'
import React, { createElement, type ReactNode } from 'react'
import RbacAccessDeniedPage from '~/components/communication/RbacAccessDeniedPage'
import { frenchPluralSuffix } from '~/lib/utils'
import { useAbility, type AppAbility, type PermissionTuple } from '~/utils/ability'
import { describePermission } from '~/utils/permissionLabels'
import type { FileRoutesByTo } from '../routeTree.gen'

type RouteTo = Extract<keyof FileRoutesByTo, string>

/** `staticData.permission` accepte un tuple unique ou un tableau de tuples (tous requis, cf. below). */
type RoutePermission = PermissionTuple | PermissionTuple[]

/**
 * Normalise en liste de tuples. Un tuple unique a son premier élément = action (string) ; un tableau
 * de tuples a son premier élément = tuple (array) — d'où la discrimination sur `Array.isArray`.
 */
const normalizePermissions = (permission: RoutePermission): PermissionTuple[] =>
  Array.isArray(permission[0]) ? (permission as PermissionTuple[]) : [permission as PermissionTuple]

export function useRouteStaticData(to: RouteTo): StaticDataRouteOption | undefined {
  const router = useRouter()
  type FileRouteData = FileRoutesByTo[keyof FileRoutesByTo]
  // @ts-expect-error
  const routeData = (router.routesByPath[to] ?? router.routesByPath[`${to}/`]) as FileRouteData
  return routeData.options.staticData
}

type MUILinkProps = Omit<LinkProps, 'children'> & {
  children: ReactNode
  authorized: boolean
  /** Droits requis manquants — affichés dans le tooltip quand `authorized` est faux. */
  missingPermissions?: PermissionTuple[]
} & Pick<ButtonProps, 'variant' | 'sx' | 'color'>

const PERMISSION_DENIED_MESSAGE = "Vous n'êtes pas autorisé à effectuer cette action"

/** Contenu du tooltip de refus : message + liste des droits exacts à obtenir. */
function PermissionDeniedTooltip({
  missingPermissions,
}: {
  missingPermissions?: PermissionTuple[]
}) {
  const described = (missingPermissions ?? []).map(describePermission)
  const plural = frenchPluralSuffix(described.length)

  return (
    <div className="text-xs">
      <p>{PERMISSION_DENIED_MESSAGE}</p>
      {described.length > 0 && (
        <>
          <p className="mt-2 font-semibold">
            Droit{plural} manquant{plural} :
          </p>
          <ul className="mt-1 flex flex-col gap-0.5">
            {described.map((permission) => (
              <li key={permission.key}>
                <span className="font-medium">{permission.actionLabel}</span>
                {' — '}
                {permission.segments.join(' › ')}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

type MuiLinkComponentWithHiddenAuthorized = React.ForwardRefExoticComponent<
  Omit<MUILinkProps, 'authorized' | 'missingPermissions'> & React.RefAttributes<HTMLAnchorElement>
>
const MUILinkComponent = React.forwardRef<HTMLAnchorElement, MUILinkProps>((props, ref) => {
  // `authorized`/`missingPermissions` sont internes : on les retire avant de spread sur le Button.
  const { authorized, missingPermissions, ...buttonProps } = props
  const isDisabled = buttonProps.disabled || !authorized

  if (!authorized) {
    return (
      <Tooltip title={<PermissionDeniedTooltip missingPermissions={missingPermissions} />}>
        <span>
          <Button ref={ref} component="a" {...buttonProps} disabled />
        </span>
      </Tooltip>
    )
  }
  return (
    <Button ref={ref} component="a" {...buttonProps} disabled={isDisabled}>
      {buttonProps.children as ReactNode}
    </Button>
  )
})

const CreatedLinkComponent = createLink(MUILinkComponent)

/** Disclaimer: only works with absolute urls */
export const AuthorizedButtonLink: LinkComponent<MuiLinkComponentWithHiddenAuthorized> = (
  props,
) => {
  const staticData = useRouteStaticData(props.to as RouteTo)
  const ability = useAbility()

  const permission = staticData?.permission ?? null
  // Sémantique ALL : on collecte les tuples manquants (autorisé ssi aucun ne manque).
  const missingPermissions = permission
    ? normalizePermissions(permission).filter((p) => ability.cannot(...p))
    : []
  const authorized = missingPermissions.length === 0
  const newProps = { ...props, authorized, missingPermissions }

  return <CreatedLinkComponent {...newProps} />
}

export class PermissionRouteError extends Error {
  /** Toutes les permissions manquantes (sémantique ALL) — affichées par la page d'accès refusé. */
  readonly missingPermissions: PermissionTuple[]

  constructor(missingPermissions: PermissionTuple[]) {
    super(`Missing permissions: ${missingPermissions.map((p) => p.join('.')).join(', ')}`)
    this.name = 'PermissionRouteError'
    this.missingPermissions = missingPermissions
  }
}

export function withRoutePermissions<T extends AnyRoute>(route: T): T {
  const opts = route.options
  const _beforeLoad = opts.beforeLoad
  opts.beforeLoad = function beforeLoad(this: any, ctx: any) {
    const permission: RoutePermission | undefined = this.staticData?.permission
    if (permission) {
      const ability: AppAbility = ctx.context.ability
      // Sémantique ALL : on collecte TOUS les tuples manquants pour les afficher à l'utilisateur.
      const missing = normalizePermissions(permission).filter((p) => ability.cannot(...p))
      if (missing.length > 0) {
        throw new PermissionRouteError(missing)
      }
    }
    return _beforeLoad?.call(this, ctx)
  }
  const _errorComponent = opts.errorComponent
  opts.errorComponent = function errorComponent(props: any) {
    if (_errorComponent) {
      return createElement(_errorComponent, props)
    }
    if (props.error instanceof PermissionRouteError) {
      return <RbacAccessDeniedPage missingPermissions={props.error.missingPermissions} />
    }
    throw props.error
  }
  return route
}
